import { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/utils/auth';
import { api } from '@/utils/api';

interface CloudAccount {
    id: string;
    provider: string;
    accountEmail: string;
    lastSyncedAt?: string;
}

export function Settings() {
    const { user, refreshUser } = useAuth();
    const [name, setName] = useState('');
    const [cloudAccounts, setCloudAccounts] = useState<CloudAccount[]>([]);
    const [isNextcloudModalOpen, setIsNextcloudModalOpen] = useState(false);
    const [nextcloudConfig, setNextcloudConfig] = useState({
        serverUrl: '',
        username: '',
        password: '',
    });

    useEffect(() => {
        if (user) {
            setName(user.name);
        }
        loadCloudAccounts();
    }, [user]);

    const loadCloudAccounts = async () => {
        try {
            const accounts = await api.getConnectors();
            setCloudAccounts(accounts);
        } catch (error) {
            console.error('Failed to load cloud accounts:', error);
        }
    };

    const handleUpdateProfile = async () => {
        try {
            await api.updateUser({ name });
            await refreshUser();
            alert('Profile updated successfully');
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert('Failed to update profile');
        }
    };

    const handleConnectNextcloud = async () => {
        try {
            await api.connectNextcloud(nextcloudConfig);
            setIsNextcloudModalOpen(false);
            setNextcloudConfig({ serverUrl: '', username: '', password: '' });
            await loadCloudAccounts();
            alert('Nextcloud connected successfully');
        } catch (error) {
            console.error('Failed to connect Nextcloud:', error);
            alert('Failed to connect Nextcloud');
        }
    };

    const handleConnectGoogleDrive = () => {
        window.location.href = '/api/connectors/googledrive/auth';
    };

    const handleConnectOneDrive = () => {
        window.location.href = '/api/connectors/onedrive/auth';
    };

    const handleDisconnect = async (id: string) => {
        if (!confirm('Are you sure you want to disconnect this account?')) return;

        try {
            await api.disconnectConnector(id);
            await loadCloudAccounts();
            alert('Account disconnected');
        } catch (error) {
            console.error('Failed to disconnect:', error);
            alert('Failed to disconnect account');
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            <main style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-2xl)' }}>
                <div className="container" style={{ maxWidth: '800px' }}>
                    <h1 style={{ marginBottom: 'var(--spacing-xl)' }}>Settings</h1>

                    {/* Profile Section */}
                    <section style={{ marginBottom: 'var(--spacing-2xl)' }}>
                        <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Profile</h2>
                        <div className="panel">
                            <div className="flex flex-col gap-lg">
                                <Input
                                    label="Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    label="Email"
                                    value={user?.email || ''}
                                    disabled
                                    style={{ opacity: 0.6 }}
                                />
                                <div>
                                    <Button variant="primary" onClick={handleUpdateProfile}>
                                        Save Changes
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Cloud Storage Section */}
                    <section>
                        <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Cloud Storage</h2>
                        <div className="panel">
                            <p style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)' }}>
                                Connect your cloud storage accounts to sync your scripts automatically.
                            </p>

                            {/* Connected Accounts */}
                            {cloudAccounts.length > 0 && (
                                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                    <h3 style={{ fontSize: '1rem', marginBottom: 'var(--spacing-md)' }}>
                                        Connected Accounts
                                    </h3>
                                    <div className="flex flex-col gap-md">
                                        {cloudAccounts.map(account => (
                                            <Card key={account.id}>
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div style={{ fontWeight: 500, marginBottom: 'var(--spacing-xs)' }}>
                                                            {account.provider.charAt(0).toUpperCase() + account.provider.slice(1)}
                                                        </div>
                                                        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                                            {account.accountEmail}
                                                        </div>
                                                        {account.lastSyncedAt && (
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                                                                Last synced: {new Date(account.lastSyncedAt).toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Button variant="ghost" onClick={() => handleDisconnect(account.id)}>
                                                        Disconnect
                                                    </Button>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Connect New Account */}
                            <div>
                                <h3 style={{ fontSize: '1rem', marginBottom: 'var(--spacing-md)' }}>
                                    Add Connection
                                </h3>
                                <div className="flex flex-col gap-md">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setIsNextcloudModalOpen(true)}
                                        style={{ justifyContent: 'flex-start' }}
                                    >
                                        Connect Nextcloud
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleConnectGoogleDrive}
                                        style={{ justifyContent: 'flex-start' }}
                                    >
                                        Connect Google Drive
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleConnectOneDrive}
                                        style={{ justifyContent: 'flex-start' }}
                                    >
                                        Connect OneDrive
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            {/* Nextcloud Connection Modal */}
            <Modal
                open={isNextcloudModalOpen}
                onClose={() => setIsNextcloudModalOpen(false)}
                title="Connect Nextcloud"
            >
                <div className="flex flex-col gap-lg">
                    <Input
                        label="Server URL"
                        value={nextcloudConfig.serverUrl}
                        onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, serverUrl: e.target.value })}
                        placeholder="https://cloud.example.com"
                    />
                    <Input
                        label="Username"
                        value={nextcloudConfig.username}
                        onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, username: e.target.value })}
                    />
                    <Input
                        label="App Password"
                        type="password"
                        value={nextcloudConfig.password}
                        onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, password: e.target.value })}
                    />
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginTop: 0 }}>
                        Use an app-specific password from your Nextcloud security settings
                    </p>
                    <div className="flex gap-md" style={{ justifyContent: 'flex-end' }}>
                        <Button variant="ghost" onClick={() => setIsNextcloudModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConnectNextcloud}
                            disabled={!nextcloudConfig.serverUrl || !nextcloudConfig.username || !nextcloudConfig.password}
                        >
                            Connect
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
